import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Typography,
  Card,
  Box,
  TableContainer,
  CircularProgress,
  MenuItem,
  TextField,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { StatusCodes } from 'http-status-codes';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';

import {
  axiosInstance,
  axiosInstanceForTenantAPI,
} from '../../../../assets/utils/axiosInstance';
import { formatDate } from '../../../../assets/utils/formatDate';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import { FinalReportData } from '../../../../interfaces/FinalReportData';
import { LooseObject } from '../../../../interfaces/LooseObject';
import { ReportTypeProp } from '../../../../interfaces/ReportFormType';
import ConstrainWrapper from '../../../common/constrainWrapper';
import HeaderTextComponent from '../../../common/headerTextComponent';
import StackPagination from '../../../common/stackPagination';

import { usePriorityOptions } from '~/hooks/fetcher/usePriorityOptions';
import { useAuthSlice } from '~/slices/auth';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';

interface FinalReportListResponse {
  count: number;
  results: FinalReportData[];
}

interface ListFilterParams {
  severity: string;
  page: number;
  reportType: string;
  searchQuery: string;
}

interface ReportTypeOption {
  id: number;
  label: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    justifyContent: 'center',
    height: '56rem',
  },
  tableContainer: {
    maxHeight: '51rem',
    overflow: 'auto',
    border: '1px solid rgba(0, 0, 0, 0.16)',
  },
  tableHead: {
    fontFamily: 'Schibsted Grotesk',
    fontSize: '14px',
    backgroundColor: 'white',
    color: 'grey',
  },
  tableBody: {
    fontSize: '14px',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.24)',
      borderRadius: '4px',
    },
  },
  tableCellHeader: {
    color: 'gray',
    fontSize: '14px',
  },
});

const FinalReportsListTable = () => {
  const FIRST_PAGE_NUMBER = 0;
  const NO_OF_COLS = 7;
  const DEBOUNCE_DURATION_MS = 450;

  const DEFAULT_VALUE = 'Default';
  const REPORT_LIST_URL = '/final-reports-list';

  const DEFAULT_OPTION = {
    id: 0,
    label: DEFAULT_VALUE,
  };
  const { slug } = useAuthSlice().auth.user;

  const classes = useStyles();
  const navigate = useNavigate();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;

  const [currentPage, setCurrentPage] = useState(FIRST_PAGE_NUMBER);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState(DEFAULT_VALUE);
  const [selectedReportType, setSelectedReportType] =
    useState<ReportTypeOption>(DEFAULT_OPTION);
  const { createToastNotification } = useToastNotificationContext();
  const { priorityOptions } = usePriorityOptions();

  const filterParams: ListFilterParams = {
    severity: selectedSeverity,
    page: currentPage,
    reportType: selectedReportType.label,
    searchQuery: searchQuery,
  };

  const listFetcher = async (url: string, queryParams: ListFilterParams) => {
    const reqBody: LooseObject = {
      page: queryParams.page + 1,
    };
    if (queryParams.severity !== DEFAULT_VALUE) {
      reqBody.report_severity = queryParams.severity;
    }
    if (queryParams.reportType !== DEFAULT_VALUE) {
      reqBody.report_type = queryParams.reportType;
    }
    if (queryParams.searchQuery) {
      reqBody.search = queryParams.searchQuery;
    }
    const response =
      await axiosInstanceForTenantAPI().get<FinalReportListResponse>(url, {
        params: reqBody,
      });
    if (response.status !== StatusCodes.OK) {
      const error = new Error(
        'An error occurred while fetching final reports.',
      );
      createToastNotification(error.message, 'error');
    }
    return response.data;
  };

  const reportTypeFetcher = async (url: string) => {
    const response = await axiosInstance().get<{
      report_types: ReportTypeProp[];
    }>(url);
    if (response.status !== StatusCodes.OK) {
      const error = new Error('An error occurred while fetching report types.');
      createToastNotification(error.message, 'error');
    }
    return response.data;
  };

  const {
    data: listData,
    error: listError,
    isLoading: listLoading,
  } = useSWR([REPORT_LIST_URL, filterParams], ([url, params]) =>
    listFetcher(url, params),
  );

  const { data } = useSWR(
    `/intake-channel/tenant/${slug}/${selectedChannel}`,
    reportTypeFetcher,
  );

  const handlePageChange = async (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ): Promise<void> => {
    setCurrentPage(newPage);
  };

  const handleSeverityChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSelectedSeverity(event.target.value);
    setCurrentPage(FIRST_PAGE_NUMBER);
  };

  const handleReportTypeAutocompleteChange = (
    value: ReportTypeOption | null,
  ) => {
    setSelectedReportType(value || DEFAULT_OPTION);
    setCurrentPage(FIRST_PAGE_NUMBER);
  };

  const searchList = useRef(
    _.debounce((query: string) => {
      setSearchQuery(query);
      setCurrentPage(FIRST_PAGE_NUMBER);
    }, DEBOUNCE_DURATION_MS),
  ).current;

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    searchList(event.target.value);
  };

  const handleRowClicked = (id: number | undefined) => {
    if (!id) {
      return;
    }
    navigate(`${id}`);
  };

  let content = <></>;

  if (listLoading) {
    content = (
      <TableRow>
        <TableCell
          colSpan={NO_OF_COLS}
          sx={{
            border: 0,
          }}
        >
          <Box
            width='100%'
            height='100%'
            display='flex'
            justifyContent='center'
            alignItems='center'
            flexGrow={1}
          >
            <CircularProgress />
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  if (listError && !listData) {
    content = (
      <TableRow>
        <TableCell colSpan={NO_OF_COLS}>
          <Typography>
            <FormattedMessage defaultMessage='An error occurred. Please try again later.' />
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  if (listData && listData.results.length === 0) {
    content = (
      <TableRow>
        <TableCell
          colSpan={NO_OF_COLS}
          sx={{
            border: 0,
          }}
        >
          <Typography textAlign='center' color='#878787'>
            <FormattedMessage defaultMessage='No finalized reports.' />
          </Typography>
        </TableCell>
      </TableRow>
    );
  }

  if (listData && listData.results.length > 0) {
    content = (
      <>
        {listData.results.map((report) => (
          <TableRow
            key={report.id}
            hover={true}
            component='tr'
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => handleRowClicked(report.id)}
          >
            <TableCell>
              <ConstrainWrapper
                sx={{
                  color: '#181818',
                }}
                maxWidth='125px'
              >
                {report.template_name}
              </ConstrainWrapper>
            </TableCell>
            <TableCell>
              <ConstrainWrapper
                sx={{
                  color: '#181818',
                }}
                maxWidth='100px'
              >
                {report.case_manager_name === ''
                  ? 'N/A'
                  : report.case_manager_name}
              </ConstrainWrapper>
            </TableCell>
            <TableCell>
              <ConstrainWrapper
                sx={{
                  color: '#181818',
                }}
                maxWidth='100px'
              >
                {report.report_reference === '' ? 'N/A' : report.report_number}
              </ConstrainWrapper>
            </TableCell>
            <TableCell>
              <ConstrainWrapper
                sx={{
                  color: '#181818',
                }}
                maxWidth='100px'
              >
                {report.report_type === '' ? 'N/A' : report.report_type}
              </ConstrainWrapper>
            </TableCell>
            <TableCell>
              <ConstrainWrapper
                sx={{
                  color: '#181818',
                }}
                maxWidth='100px'
              >
                {report.report_severity === '' ? 'N/A' : report.report_severity}
              </ConstrainWrapper>
            </TableCell>
            <TableCell>
              <ConstrainWrapper
                sx={{
                  color: '#181818',
                }}
                maxWidth='100px'
              >
                {report.date_received
                  ? formatDate(report.date_received)
                  : 'N/A'}
              </ConstrainWrapper>
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  }

  useEffect(() => {
    return () => {
      searchList.cancel();
    };
  }, [searchList]);

  const reversedOptions = [...priorityOptions].reverse();
  const sortedReportTypes = data
    ? data.report_types.sort((a, b) => a.name.localeCompare(b.name))
    : [];
  const mappedReportTypes: ReportTypeOption[] = sortedReportTypes.map(
    (reportType, index) => {
      return {
        id: index + 1,
        label: reportType.name,
      };
    },
  );
  const autoCompleteOptions =
    mappedReportTypes.length > 0
      ? [DEFAULT_OPTION, ...mappedReportTypes]
      : mappedReportTypes;

  return (
    <Box>
      <Card
        sx={{
          borderTopLeftRadius: '0px',
          borderTopRightRadius: '0px',
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          borderLeft: '1px solid #00000029',
          borderRight: '1px solid #00000029',
          borderBottom: '1px solid #00000029',
          padding: '24px 0px',
          boxShadow: '0px 0px 4px 0px #0000000A',
          width: '100%',
          backgroundColor: '#FFF',
          height: '100%',
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 2, margin: '0px 24px 0px 20px' }}
        >
          <HeaderTextComponent variant='h1'>
            <FormattedMessage defaultMessage='List of All Finalized Reports' />
          </HeaderTextComponent>
          <Stack
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0px 10px 0px 0px',
              padding: '0px 10px 0px 10px',
            }}
            direction='row'
          >
            <TextField
              select
              onChange={handleSeverityChange}
              variant='standard'
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position='start'>
                    <Typography fontSize={14}>
                      <FormattedMessage defaultMessage='Severity:' />
                    </Typography>
                  </InputAdornment>
                ),
              }}
              SelectProps={{
                IconComponent: ExpandMoreIcon,
                sx: {
                  fontSize: '14px',
                },
                SelectDisplayProps: {
                  style: {
                    paddingLeft: '4px',
                  },
                },
              }}
              sx={{
                borderRadius: '6px',
                border: '1px solid #D1D1D1',
                backgroundColor: 'white',
                padding: '5px 10px',
                margin: '0px 0px 0px 4px',
                minWidth: '200px',
              }}
              value={selectedSeverity}
            >
              {reversedOptions.map((option) => (
                <MenuItem
                  value={option.label}
                  key={option.value}
                  sx={{
                    alignItems: 'center',
                    fontSize: '14px',
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {autoCompleteOptions.length > 0 && (
              <Autocomplete
                sx={{ width: '275px', marginX: 1 }}
                options={autoCompleteOptions}
                value={selectedReportType}
                onChange={(__, newValue) =>
                  handleReportTypeAutocompleteChange(newValue)
                }
                slotProps={{
                  paper: {
                    sx: {
                      fontSize: 14,
                    },
                  },
                }}
                isOptionEqualToValue={(option, value) => {
                  return option.id === value.id;
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='standard'
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Typography fontSize={14}>
                            <FormattedMessage defaultMessage='Report Type:' />
                          </Typography>
                        </InputAdornment>
                      ),
                      sx: {
                        fontSize: 14,
                      },
                    }}
                    sx={{
                      borderRadius: '6px',
                      border: '1px solid #D1D1D1',
                      backgroundColor: 'white',
                      padding: '5px 10px',
                      margin: '0px 4px 0px 4px',
                      minWidth: '175px',
                    }}
                  />
                )}
              />
            )}

            <TextField
              variant='standard'
              hiddenLabel
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: '14px',
                },
              }}
              sx={{
                borderRadius: '6px',
                border: '1px solid #D1D1D1',
                backgroundColor: 'white',
                padding: '5px 10px',
                margin: '0px 4px 0px 4px',
                minWidth: '175px',
              }}
              placeholder='Search'
              onChange={handleSearchInputChange}
            />
          </Stack>
        </Stack>
        <TableContainer
          sx={{
            maxHeight: '770px',
            height: 'calc(100% - 18px)',
          }}
        >
          <Table sx={{ margin: 0 }} stickyHeader>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell sx={{ color: 'grey' }}>
                  <FormattedMessage defaultMessage='Template' />
                </TableCell>
                <TableCell sx={{ color: 'grey' }}>
                  <FormattedMessage defaultMessage='Case Manager' />
                </TableCell>
                <TableCell sx={{ color: 'grey' }}>
                  <FormattedMessage defaultMessage='Report Reference' />
                </TableCell>
                <TableCell sx={{ color: 'grey' }}>
                  <FormattedMessage defaultMessage='Report Type' />
                </TableCell>
                <TableCell sx={{ color: 'grey' }}>
                  <FormattedMessage defaultMessage='Severity' />
                </TableCell>
                <TableCell sx={{ color: 'grey' }}>
                  <div
                    style={{
                      marginTop: '15px',
                      fontSize: '14px',
                      fontFamily: 'Schibsted Grotesk',
                    }}
                  >
                    <FormattedMessage defaultMessage='Date Received' />
                  </div>
                  <div style={{ fontSize: '12px' }}>(dd-mm-yy)</div>{' '}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>{content}</TableBody>
          </Table>
        </TableContainer>
      </Card>
      {listData && (
        <StackPagination
          count={listData.count}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      )}
    </Box>
  );
};

export default FinalReportsListTable;
